import { useCallback, useEffect, useRef, useState } from "react";
import AgendaService, {
  AgendaItem,
  AgendaResponse
} from "../../../services/AgendaService";
import CompanyService from "../../../services/CompanyService";
import { TranslationService } from "../../../services/TranslationService";
import ModalService from "../../shared/bootstrap/modal/Modal";
import { ToastService } from "../../shared/bootstrap/Toast";
import { openAgendaActionFlow } from "../utils/agendaActionFlows";
import { translateWith } from "../utils/agendaFormat";
import { canRunAgendaAction } from "../utils/agendaPermissions";
import { isCompleted, isInsufficientBalanceError } from "../utils/agendaUtils";

export interface UseAgendaResult {
  agenda?: AgendaResponse;
  items: AgendaItem[];
  loading: boolean;
  loadError: boolean;
  /** True while generate / regenerate is running (30-70 s). */
  generating: boolean;
  /** QueriesOnly users can view the agenda but cannot change it. */
  canEdit: boolean;
  selectedItem?: AgendaItem;
  selectItem: (item: AgendaItem) => void;
  clearSelection: () => void;
  reload: () => Promise<void>;
  generate: () => Promise<void>;
  requestRegenerate: () => void;
  requestToggleCompleted: (item: AgendaItem) => void;
  /** Completes an item without asking (used after an action flow was saved). */
  completeItem: (item: AgendaItem) => Promise<boolean>;
  requestDelete: (item: AgendaItem) => void;
  /** Closes the sidebar and opens the existing app flow with the edited draft. */
  runPrimaryAction: (
    item: AgendaItem,
    subject: string,
    content: string
  ) => Promise<void>;
}

const showError = (message?: string) =>
  ToastService.showToast(
    message ?? TranslationService.translate.ErrorProcessingRequest,
    undefined,
    "danger"
  );

export function useAgenda(): UseAgendaResult {
  const [agenda, setAgenda] = useState<AgendaResponse>();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<number>();
  const isMounted = useRef(true);
  const canEdit = CompanyService.canEditAgenda();

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const reload = useCallback(async () => {
    const result = await AgendaService.get();
    if (!isMounted.current) {
      return;
    }
    if (result instanceof Error) {
      setLoadError(true);
    } else {
      setLoadError(false);
      setAgenda(result);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const generateWith = useCallback(
    async (force: boolean) => {
      setGenerating(true);
      const result = await AgendaService.generate(force);
      if (result instanceof Error) {
        showError(
          isInsufficientBalanceError(result)
            ? TranslationService.translate.AgendaInsufficientBalance
            : undefined
        );
      } else {
        // Both "generated" and "skipped" leave today's agenda ready to read.
        await reload();
      }
      if (isMounted.current) {
        setGenerating(false);
      }
    },
    [reload]
  );

  const generate = useCallback(() => generateWith(false), [generateWith]);

  const confirm = (
    titleKey: string,
    messageKey: string,
    acceptLabel: string,
    onAccept: () => void,
    values: Record<string, string> = {},
    danger = false
  ) =>
    ModalService.showDefaultModal({
      title: TranslationService.translate[titleKey],
      message: translateWith(messageKey, values),
      acceptButtonLabel: acceptLabel,
      acceptButtonClassName: danger ? "btn btn-danger" : undefined,
      onAcceptClick: onAccept
    });

  const requestRegenerate = () => {
    if (!canEdit) {
      return;
    }
    confirm(
      "AgendaRegenerateConfirmTitle",
      "AgendaRegenerateConfirmMessage",
      TranslationService.translate.AgendaRegenerate,
      () => {
        generateWith(true);
      }
    );
  };

  const applyUpdate = (updated: AgendaItem) =>
    setAgenda((previous) =>
      previous
        ? {
            ...previous,
            items: previous.items.map((x) =>
              x.itemId === updated.itemId ? updated : x
            )
          }
        : previous
    );

  const setCompleted = async (item: AgendaItem, completed: boolean) => {
    const result = await AgendaService.updateItem(item.itemId, {
      completado: completed
    });
    if (result instanceof Error) {
      showError();
      return false;
    }
    applyUpdate(result);
    return true;
  };

  const requestToggleCompleted = (item: AgendaItem) => {
    if (!canEdit) {
      return;
    }
    const completing = !isCompleted(item);
    confirm(
      completing ? "AgendaCompleteConfirmTitle" : "AgendaReopenConfirmTitle",
      completing
        ? "AgendaCompleteConfirmMessage"
        : "AgendaReopenConfirmMessage",
      completing
        ? TranslationService.translate.AgendaComplete
        : TranslationService.translate.AgendaReopen,
      () => {
        setCompleted(item, completing);
      },
      { name: item.nombre ?? "" }
    );
  };

  const completeItem = async (item: AgendaItem) =>
    canEdit && !isCompleted(item) ? setCompleted(item, true) : false;

  const requestDelete = (item: AgendaItem) => {
    if (!canEdit) {
      return;
    }
    confirm(
      "AgendaDeleteConfirmTitle",
      "AgendaDeleteConfirmMessage",
      TranslationService.translate.Delete,
      async () => {
        const result = await AgendaService.deleteItem(item.itemId);
        if (result instanceof Error) {
          showError();
          return;
        }
        setAgenda((previous) =>
          previous
            ? {
                ...previous,
                items: previous.items.filter((x) => x.itemId !== item.itemId)
              }
            : previous
        );
        setSelectedItemId((current) =>
          current === item.itemId ? undefined : current
        );
      },
      { name: item.nombre ?? "" },
      true
    );
  };

  const runPrimaryAction = async (
    item: AgendaItem,
    subject: string,
    content: string
  ) => {
    if (!canEdit || !canRunAgendaAction(item.accion)) {
      return;
    }
    setSelectedItemId(undefined);
    await openAgendaActionFlow({
      item,
      subject,
      content,
      // The flow saved successfully: complete the item without asking again.
      onSaved: () => {
        completeItem(item);
      }
    });
  };

  const items = agenda?.items ?? [];

  return {
    agenda,
    items,
    loading,
    loadError,
    generating,
    canEdit,
    selectedItem: items.find((x) => x.itemId === selectedItemId),
    selectItem: (item) => setSelectedItemId(item.itemId),
    clearSelection: () => setSelectedItemId(undefined),
    reload,
    generate,
    requestRegenerate,
    requestToggleCompleted,
    completeItem,
    requestDelete,
    runPrimaryAction
  };
}
